import R from 'app/assets/R'
import {
  IResponseFilter,
  initResponseFilterTableManagement,
} from 'app/components/tables/BaseTableManagement/FilterManagementTable/type'
import {
  RenderValueTable,
  StatusTableEnum,
  useHookTableManagement,
} from 'app/components/tables/BaseTableManagement/hook'
import {
  ExpandedTableValue,
  convertColumnTable,
} from 'app/components/tables/BaseTableReport/hook'
import { IDataBaseTable } from 'app/components/tables/BaseTableReport/type'
import { isEmpty, isEqual } from 'lodash'
import { Pagination } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Delete } from './components/Delete'
import { IGeneralItemCategoryManagementTable } from './type'

import { requestGetItemCategoryManagement } from 'app/api/item-category'
import { ItemCategoryMessageCode } from 'app/api/item-category/constant'
import {
  IParamsGetItemCategoryManagement,
  IResponseGetItemCategoryManagement,
} from 'app/api/item-category/model/management'
import {
  convertArrayStatusFilterToArrayStatusNumber,
  convertStatusToFilterStatus,
} from 'app/common/helpers'
import {
  FunctionPermissionEnum,
  ModulePermissionEnum,
} from 'app/common/permission-module'
import { useVerifyPermissionAndRedirect } from 'app/common/use-verify-permission-redirect'
import { initPagination } from 'app/constant'
import { Create } from './components/Create'
import { Update } from './components/Update'

export const useHookTable = () => {
  const { t } = useTranslation()

  const [filter, setFilter] = useState<IResponseFilter>(
    initResponseFilterTableManagement,
  )

  const { isHavePermissionFunctionAndGoBack } = useVerifyPermissionAndRedirect()

  useEffect(() => {
    isHavePermissionFunctionAndGoBack(
      ModulePermissionEnum.INVENTORY,
      FunctionPermissionEnum.CATEGORY_READ,
      R.strings.role,
    )
  }, [])

  const getDataTable = async (
    pagination: Pagination,
    newFilter?: IResponseFilter,
  ) => {
    const initData = {
      pagination: pagination ?? initPagination,
      data: [],
    } as IDataBaseTable<IGeneralItemCategoryManagementTable>
    try {
      const status = convertArrayStatusFilterToArrayStatusNumber(
        newFilter?.status ?? filter?.status,
      )
      let params: IParamsGetItemCategoryManagement = {
        page: pagination?.current,
        pagesize: pagination?.pageSize,
        keyword: newFilter?.search ?? filter?.search,
      }

      params = { ...params, ...newFilter, status }

      const response: IResponseGetItemCategoryManagement =
        await requestGetItemCategoryManagement(params)

      if (
        isEqual(
          response?.msgcode,
          ItemCategoryMessageCode.ItemCategoryManagement.successGetData,
        )
      ) {
        const data = response?.data?.rows
        const metadata = response?.data?.metadata

        const startIndex =
          ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 10)

        const transformData = (data, isChildren = false) => {
          if (isEmpty(data)) {
            return data
          }

          return data?.map((item, index) => {
            return {
              key: `${item?._id}-${index + 1}`,
              ordinalNumber: isChildren ? null : startIndex + index + 1,
              name: item?.name ?? '-',
              _id: item?._id ?? '-',
              status: convertStatusToFilterStatus(item?.status),
              children: !isEmpty(item?.children)
                ? transformData(item?.children, true)
                : undefined,
            }
          })
        }

        const dataTables = transformData(data)

        return {
          pagination: {
            current: metadata?.currentPage ?? 1,
            pageSize: pagination?.pageSize,
            total: metadata?.totalItem ?? 0,
          },
          data: dataTables,
        } as IDataBaseTable<IGeneralItemCategoryManagementTable>
      }

      return initData
    } catch (error) {
      return initData
    }
  }

  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([])

  const handleExpand = (expanded: boolean, newKey?: number) => {
    if (!newKey) return
    const isKeyExpanded = expandedRowKeys.includes(newKey)

    if (!isKeyExpanded) {
      // If the key is not in the array and the row is expanding, add it
      setExpandedRowKeys([...expandedRowKeys, newKey])
    } else {
      // If the key is in the array and the row is collapsing, remove it
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== newKey))
    }
  }

  const { tableData, selectRowsTable, handleTableChange, fetch, rowSelection } =
    useHookTableManagement({ getDataTable })

  const handleChangeFilter = (newFilter: IResponseFilter) => {
    if (!isEqual(newFilter, filter)) {
      setFilter(newFilter)
      fetch(
        {
          ...tableData.pagination,
          current: initPagination.current,
        },
        newFilter,
      )
    }
  }

  const fetchNewData = () => {
    fetch(initPagination)
    setFilter(initResponseFilterTableManagement)
  }

  const columns = useMemo(() => {
    const option = [
      // convertColumnTable<IGeneralItemCategoryManagementTable>({
      //   title: t(R.strings.ordinal_number),
      //   fixed: 'left',
      //   key: 'ordinalNumber',
      //   classNameWidthColumnOverwrite: 'number-column',
      //   render: (_, record) => (
      //     <RenderValueTable value={record.ordinalNumber} type="Base" />
      //   ),
      // }),
      convertColumnTable<IGeneralItemCategoryManagementTable>({
        title: t(R.strings.skill_content_name),
        fixed: 'left',
        key: 'name',
        className: 'column-children',
        classNameWidthColumnOverwrite: 'big-column',
        render: (_, record) => {
          const isExpanded = expandedRowKeys.includes(record.key ?? 0)
          const notExpand = isEmpty(record.children)

          const _handleExpand = () => {
            return handleExpand(!notExpand, record?.key)
          }

          return (
            <ExpandedTableValue
              isExpanded={isExpanded}
              value={record.name?.toString()}
              isNotHaveActionExpanded={notExpand}
              handleExpand={_handleExpand}
            />
          )
        },
      }),
      convertColumnTable<IGeneralItemCategoryManagementTable>({
        title: t(R.strings.status),
        key: 'status',
        classNameWidthColumnOverwrite: 'small-column',
        render: (_, record) => (
          <RenderValueTable
            value={record.status}
            type="Status"
            actionComponent={
              <>
                <Create
                  fetchTable={fetchNewData}
                  parentId={record?._id || undefined}
                />
                <Update id={record?._id} fetchTable={fetchNewData} />
                {!isEqual(record?.status, StatusTableEnum.deleted) && (
                  <Delete
                    id={record?._id}
                    fetchTable={fetchNewData}
                    name={record?.name}
                  />
                )}
              </>
            }
          />
        ),
      }),
    ]
    return option
  }, [t, fetchNewData])

  return {
    tableData,
    selectRowsTable,
    handleTableChange,
    fetchNewData,
    rowSelection,
    columns,
    handleChangeFilter,
    filter,
    expandedRowKeys,
    handleExpand,
  }
}
